// @ts-nocheck
/**
 * Profile Settings Page - Epic 87
 * Universal profile management for all user roles
 * Carbon Design System compliant
 * All styling uses Carbon Design tokens via SCSS module
 */

import React, { useState, useEffect } from 'react';
import {
  Content,
  Grid,
  Column,
  Heading,
  Stack,
  Button,
  Form,
  TextInput,
  Select,
  SelectItem,
  FileUploaderDropContainer,
  InlineNotification,
  Loading,
  Modal,
  Tile,
} from '@carbon/react';
import { User, Save, TrashCan, CloudUpload } from '@carbon/icons-react';
import { useNavigate } from 'react-router-dom';
import styles from './ProfileSettingsPage.module.scss';

interface UserProfile {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  profile_image_url: string | null;
  timezone: string;
  language: string;
  role: string;
  subscription_type: string | null;
  created_at: number;
  updated_at: number;
}

const ProfileSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    timezone: 'UTC',
    language: 'en',
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleteConfirmation, setDeleteConfirmation] = useState('');

  // Load profile on mount
  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try multiple token keys for compatibility
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('sigma08_jwt_token');

      if (!token) {
        navigate('/auth/login');
        return;
      }

      // BUGFIX: Use internal API route instead of external domain
      const response = await fetch('/api/v1/auth/profile', {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 401) {
          localStorage.removeItem('auth_token');
          navigate('/auth/login');
          return;
        }
        throw new Error('Failed to load profile');
      }

      const data = await response.json();
      setProfile(data.profile);
      setFormData({
        first_name: data.profile.first_name || '',
        last_name: data.profile.last_name || '',
        timezone: data.profile.timezone || 'UTC',
        language: data.profile.language || 'en',
      });
    } catch (err) {
      setError('Failed to load profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Try multiple token keys for compatibility
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('sigma08_jwt_token');

      if (!token) {
        navigate('/auth/login');
        return;
      }

      // BUGFIX: Use internal API route instead of external domain
      const response = await fetch('/api/v1/auth/profile', {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to update profile');
      }

      const data = await response.json();
      setProfile(data.profile);
      setSuccess('Profile updated successfully!');

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Please upload JPG, PNG, or WebP image.');
      return;
    }

    // Validate file size (5MB max)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setError('File size exceeds 5MB limit.');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      // Try multiple token keys for compatibility
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('sigma08_jwt_token');

      if (!token) {
        navigate('/auth/login');
        return;
      }

      const formData = new FormData();
      formData.append('image', file);

      // BUGFIX: Use internal API route instead of external domain
      const response = await fetch('/api/v1/auth/profile/image', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to upload image');
      }

      const data = await response.json();

      // Update profile with new image URL
      if (profile) {
        setProfile({
          ...profile,
          profile_image_url: data.profile_image_url,
        });
      }

      setSuccess('Profile image uploaded successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'DELETE') {
      setError('Please type DELETE to confirm account deletion');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Try multiple token keys for compatibility
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('auth_token') ||
        sessionStorage.getItem('access_token') ||
        sessionStorage.getItem('sigma08_jwt_token');

      if (!token) {
        navigate('/auth/login');
        return;
      }

      // BUGFIX: Use internal API route instead of external domain
      const response = await fetch('/api/v1/auth/account', {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          password: deletePassword,
          confirmation: deleteConfirmation,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to delete account');
      }

      // Clear token and redirect to login
      localStorage.removeItem('auth_token');
      navigate('/auth/login', {
        state: { message: 'Account deleted successfully' },
      });
    } catch (err: any) {
      setError(err.message || 'Failed to delete account. Please try again.');
    } finally {
      setSaving(false);
      setDeleteModalOpen(false);
    }
  };

  if (loading) {
    return (
      <Content>
        <div className={styles['loading-container']}>
          <Loading description="Loading profile..." />
        </div>
      </Content>
    );
  }

  if (!profile) {
    return (
      <Content>
        <InlineNotification kind="error" title="Error" subtitle="Failed to load profile" />
      </Content>
    );
  }

  return (
    <Content>
      <Grid>
        {/* Page Header */}
        <Column lg={16} md={8} sm={4}>
          <Stack gap={3}>
            <Heading>Profile Settings</Heading>
            <p className={styles['page-description']}>Manage your personal information and account settings</p>
          </Stack>
        </Column>

        {/* Notifications */}
        {error && (
          <Column lg={16} md={8} sm={4} className={styles['column-spacing']}>
            <InlineNotification kind="error" title="Error" subtitle={error} onClose={() => setError(null)} />
          </Column>
        )}

        {success && (
          <Column lg={16} md={8} sm={4} className={styles['column-spacing']}>
            <InlineNotification kind="success" title="Success" subtitle={success} onClose={() => setSuccess(null)} />
          </Column>
        )}

        {/* Profile Image Section */}
        <Column lg={8} md={4} sm={4} className={styles['column-spacing-large']}>
          <Tile className={styles['tile-padded']}>
            <Stack gap={5}>
              <Heading size="sm">Profile Picture</Heading>

              <div className={styles['profile-image-container']}>
                {profile.profile_image_url ? (
                  <img src={profile.profile_image_url} alt="Profile" className={styles['profile-image']} />
                ) : (
                  <div className={styles['profile-image-placeholder']}>
                    <User size={48} className={styles['placeholder-icon']} />
                  </div>
                )}

                <div>
                  <label htmlFor="profile-image-upload">
                    <Button kind="secondary" size="md" renderIcon={CloudUpload} disabled={uploading} as="span">
                      {uploading ? 'Uploading...' : 'Change Picture'}
                    </Button>
                  </label>
                  <input
                    id="profile-image-upload"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleImageUpload}
                    className={styles['file-input-hidden']}
                  />
                  <p className={styles['image-upload-hint']}>JPG, PNG or WebP. Max 5MB.</p>
                </div>
              </div>
            </Stack>
          </Tile>
        </Column>

        {/* Account Info */}
        <Column lg={8} md={4} sm={4} className={styles['column-spacing-large']}>
          <Tile className={styles['tile-padded']}>
            <Stack gap={4}>
              <Heading size="sm">Account Information</Heading>

              <div>
                <p className={styles['info-label']}>Email</p>
                <p className={styles['info-value']}>{profile.email}</p>
              </div>

              <div>
                <p className={styles['info-label']}>Role</p>
                <p className={styles['info-value-capitalize']}>{profile.role.replace('_', ' ')}</p>
              </div>

              {profile.subscription_type && (
                <div>
                  <p className={styles['info-label']}>Subscription</p>
                  <p className={styles['info-value-capitalize']}>{profile.subscription_type}</p>
                </div>
              )}
            </Stack>
          </Tile>
        </Column>

        {/* Profile Form */}
        <Column lg={16} md={8} sm={4} className={styles['column-spacing-xlarge']}>
          <Tile className={styles['tile-padded']}>
            <Form>
              <Stack gap={6}>
                <Heading size="sm">Personal Information</Heading>

                <Grid>
                  <Column lg={8} md={4} sm={4}>
                    <TextInput
                      id="first_name"
                      labelText="First Name"
                      value={formData.first_name}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          first_name: e.target.value,
                        })
                      }
                    />
                  </Column>

                  <Column lg={8} md={4} sm={4}>
                    <TextInput
                      id="last_name"
                      labelText="Last Name"
                      value={formData.last_name}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          last_name: e.target.value,
                        })
                      }
                    />
                  </Column>

                  <Column lg={8} md={4} sm={4}>
                    <Select
                      id="timezone"
                      labelText="Timezone"
                      value={formData.timezone}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          timezone: e.target.value,
                        })
                      }
                    >
                      <SelectItem value="UTC" text="UTC" />
                      <SelectItem value="America/New_York" text="Eastern Time (ET)" />
                      <SelectItem value="America/Chicago" text="Central Time (CT)" />
                      <SelectItem value="America/Denver" text="Mountain Time (MT)" />
                      <SelectItem value="America/Los_Angeles" text="Pacific Time (PT)" />
                      <SelectItem value="Europe/London" text="London (GMT)" />
                      <SelectItem value="Europe/Paris" text="Paris (CET)" />
                      <SelectItem value="Asia/Tokyo" text="Tokyo (JST)" />
                    </Select>
                  </Column>

                  <Column lg={8} md={4} sm={4}>
                    <Select
                      id="language"
                      labelText="Language"
                      value={formData.language}
                      onChange={e =>
                        setFormData({
                          ...formData,
                          language: e.target.value,
                        })
                      }
                    >
                      <SelectItem value="en" text="English" />
                      <SelectItem value="es" text="Spanish" />
                      <SelectItem value="fr" text="French" />
                      <SelectItem value="de" text="German" />
                      <SelectItem value="zh" text="Chinese" />
                      <SelectItem value="ja" text="Japanese" />
                    </Select>
                  </Column>
                </Grid>

                <div className={styles['form-actions']}>
                  <Button kind="primary" renderIcon={Save} onClick={handleSaveProfile} disabled={saving}>
                    {saving ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              </Stack>
            </Form>
          </Tile>
        </Column>

        {/* Danger Zone */}
        <Column lg={16} md={8} sm={4} className={styles['column-spacing-xlarge']}>
          <Tile className={styles['danger-zone-tile']}>
            <Stack gap={4}>
              <Heading size="sm">Danger Zone</Heading>
              <p className={styles['danger-zone-description']}>
                Deleting your account is permanent and cannot be undone. All your data will be removed after 30 days.
              </p>
              <Button kind="danger" renderIcon={TrashCan} onClick={() => setDeleteModalOpen(true)}>
                Delete Account
              </Button>
            </Stack>
          </Tile>
        </Column>
      </Grid>

      {/* Delete Account Modal */}
      <Modal
        open={deleteModalOpen}
        modalHeading="Delete Account"
        danger
        primaryButtonText="Delete Account"
        secondaryButtonText="Cancel"
        onRequestClose={() => {
          setDeleteModalOpen(false);
          setDeletePassword('');
          setDeleteConfirmation('');
        }}
        onRequestSubmit={handleDeleteAccount}
      >
        <Stack gap={5}>
          <p>This action cannot be undone. All your data will be permanently deleted after 30 days.</p>

          <TextInput
            id="delete-password"
            type="password"
            labelText="Password"
            placeholder="Enter your password"
            value={deletePassword}
            onChange={e => setDeletePassword(e.target.value)}
          />

          <TextInput
            id="delete-confirmation"
            labelText='Type "DELETE" to confirm'
            placeholder="DELETE"
            value={deleteConfirmation}
            onChange={e => setDeleteConfirmation(e.target.value)}
          />
        </Stack>
      </Modal>
    </Content>
  );
};

export default ProfileSettingsPage;
